import React, { useEffect, useState, useMemo } from "react";

// MUI
import { Box, Button } from "@mui/material";

// Hooks
import {
  useAppDispatch,
  useAppSelector,
} from "../../../redux/hooks/redux-hooks";

// Actions
import { fetchFinances } from "../../../redux/actions/FinanceActions";
import { fetchSBGs } from "../../../redux/actions/SBGActions";
import {
  fetchSBUs,
  resetParticularUserSBU,
} from "../../../redux/actions/SBUActions";
import { fetchBUs } from "../../../redux/actions/BUActions";
import { fetchSegmentLOBs } from "../../../redux/actions/SegmentLOBActions";
import { fetchCustomers } from "../../../redux/actions/CustomerActions";
import { fetchContractTypes } from "../../../redux/actions/ContractTypeActions";
import { fetchCustomerTypes } from "../../../redux/actions/CustomerTypeActions";
import { fetchSegmentClassifications } from "../../../redux/actions/SegmentClassificationActions";
import { fetchCompetitionIntensities } from "../../../redux/actions/CompetitionIntensityActions";
import { fetchTenderingBandwidths } from "../../../redux/actions/TenderingBandwidthActions";
import { fetchGeographies } from "../../../redux/actions/GeographyActions";
import {
  setProjecId,
  setProjectObjectWithInitialValues,
} from "../../../redux/actions/ProjectActions";
import { resetDocuments } from "../../../redux/actions/BidCreationDocumentsActions";
import { postParticularProjectStatus } from "../../../redux/actions/ProjectStatusActions";
import {
  fetchAllProjectsWithStatus,
  fetchProjectsWithStatusForUserId,
} from "../../../redux/actions/ProjectResponseAction";
import { fetchAdminProjects } from "../../../redux/actions/ProjectActions";
import { setParticularProjectParticularComment } from "../../../redux/actions/ProjectCommentActions";
import { fetchQuestions } from "../../../redux/actions/WinProbabilityFactorActions";
import {
  setGoFactors,
  setGetFactors,
} from "../../../redux/actions/WinProbabilityFactorActions";
import { setBidCreationCustomerPOCs } from "../../../redux/actions/CustomerPOCActions";

// Services
import ProjectService from "../../../services/ProjectService";
import WPFService from "../../../services/WinProbabilityFactorService";
import CustomerPOCService from "../../../services/CustomerPOCService";

// Types
import {
  PostProjectStatusModel,
  Status,
} from "../../../models/PostProjectStatusModel";
import { AddEdit, Mode } from "../../../SectionWiseClassification";

// Components
import DrawerComponent from "../../DrawerComponent";
import BidPipeLineSection from "../bid-pipeline-section/BidPipeLineSection";
import Divider from "../../Divider";

// Utils
import { isValidArr } from "../../../utility/utils";

type NavProps = {
  activeStep: number;
  handlePrevious: () => void;
  handleNext: () => void;
  handleSaveAsDraft: () => void;
  isValidToGoNext: () => boolean;
  isSubmitted: undefined | boolean;
  isDrafted: undefined | boolean;
  steps: string[];
  mode: Mode;
};
const goNoGoFormNotAllowedStatuses = ['Drafted', 'Dropped', 'Dropped After GoNoGo', 'Rejected', 'Drafted', 'SavedAsDraft']

const NavButtons = ({
  activeStep,
  handlePrevious,
  handleNext,
  handleSaveAsDraft,
  isValidToGoNext,
  isSubmitted,
  isDrafted,
  steps,
  mode,
}: NavProps) => (
  <Box
    sx={{
      marginTop: "2em",
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
    }}
  >
    <Button
      color="primary"
      variant="text"
      onClick={handlePrevious}
      disabled={activeStep === 0}
    >
      Back
    </Button>
    {mode === "user" && !isSubmitted && activeStep !== steps.length - 1 && (
      <Button color="info" variant="text" onClick={handleSaveAsDraft}>
        Save as draft
      </Button>
    )}
    <Button onClick={handleNext} variant="text" disabled={!isValidToGoNext()}>
      {(activeStep === steps.length - 1) ? (isDrafted ? "Submit" : "Submit to BD Head") : "Next"}
    </Button>
  </Box>
);

type Props = {
  isOpen: boolean;
  dismissPanel: () => void;
  type: AddEdit;
  mode: Mode;
};

const BidCreationLayoutPanel = ({
  isOpen,
  dismissPanel,
  type,
  mode = "user",
}: Props) => {
  const dispatch = useAppDispatch();
  const displayToast = useAppSelector((state) => state.toast.displayToast);

  const [activeStep, setActiveStep] = useState(0);

  const allUsers = useAppSelector((state) => state.user.all_Users);
  let newUsers: { [key: string]: string } = {};
  allUsers.forEach((each) => {
    let userName = `${each.firstName}${each.lastName}`;
    userName = userName.replace(/[" "]/, "");
    newUsers[userName] = each.emailId;
  });

  const get_Project_Object = useAppSelector(
    (state) => state.project.project_Object
  );
  const projectDocuments = useAppSelector(
    (state) => state.bidCreationDocs.documents
  );

  const particularProjectResponse = useAppSelector((state) =>
    state.projectResponse.project_Response.find(
      (p) => p.projectId === get_Project_Object.projectId
    )
  );

  const projectCreationComment = useAppSelector(
    (state) => state.projectComments.project_creation_comment
  );
  const loggedInUser = useAppSelector((state) => state.user.loggedIn_User);

  const ifProjectNameExists = useAppSelector(
    (state) => state.project.projectNameExists
  );

  const wpfQuestions = useAppSelector((state) => state.wpf.questions);
  const goFactors = useAppSelector((state) => state.wpf.goFactors);
  const getFactors = useAppSelector((state) => state.wpf.getFactors);

  const projectCustomerPOCs = useAppSelector(
    (state) => state.projectCustomerPOCs.bidCreationCustomerPOCs
  );

  const steps = useMemo(() => {
    const initialSteps = [
      "Bid Details",
      "Customer POC",
      "Go Get Factor",
      "Review & Submit",
    ];
    if (((loggedInUser.roleId === 1 || loggedInUser.roleId === 2) && particularProjectResponse?.projectStatus === "Drafted") || (loggedInUser.roleId >= 5 && (particularProjectResponse?.projectStatus && goNoGoFormNotAllowedStatuses.indexOf(particularProjectResponse?.projectStatus) === -1))) {
      initialSteps.splice(3, 0, "Go No Go");
    }
    return initialSteps;
  }, [particularProjectResponse?.projectStatus]);

  const fetchProjectsOnSubmit = () => {
    setTimeout(() => {
      if (mode === "user") {
        if (loggedInUser.roleId === 5 || loggedInUser.roleId === 6) {
          dispatch(fetchAllProjectsWithStatus());
        } else {
          dispatch(
            fetchProjectsWithStatusForUserId(
              loggedInUser.userId,
              loggedInUser.roleId
            )
          );
        }
      } else {
        dispatch(fetchAdminProjects());
      }
    }, 1000);
  };

  // Handle Create Bid Panel Close
  const handleCloseBidPanel = () => {
    dispatch(setProjectObjectWithInitialValues());
    dispatch(resetDocuments());
    dispatch(setGoFactors({})); // To set the go factors to empty
    dispatch(setGetFactors({})); // To set the get factors to empty
    dispatch(resetParticularUserSBU()); // To reset particular User SBU
    dispatch(setBidCreationCustomerPOCs([])); // To reset project customer POCs
    setActiveStep(0);
    dismissPanel();
  };

  const handleNext = () => {
    if (activeStep !== steps.length - 1) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (activeStep !== 0) {
      setActiveStep((prevActiveStpe) => prevActiveStpe - 1);
    }
  };

  // Handle validation to go for next section
  const isValidToGoNext = (): boolean => {
    if (activeStep === 0) {
      if (!get_Project_Object.projectName) {
        return false;
      }
      if (ifProjectNameExists) {
        return false;
      }
      if (!get_Project_Object.sbgid) {
        return false;
      }
      if (!get_Project_Object.sbuid) {
        return false;
      }
      if (!get_Project_Object.buid) {
        return false;
      }
      if (!get_Project_Object.segmentLobid) {
        return false;
      }
      if (!get_Project_Object.customerNameId) {
        return false;
      }
      if (!get_Project_Object.segmentClassification) {
        return false;
      }
      if (!get_Project_Object.estimatedValue) {
        return false;
      }
      if (!get_Project_Object.fundingSourceId) {
        return false;
      }
      if (!get_Project_Object.contractTypeId) {
        return false;
      }
      if (!get_Project_Object.geographyId) {
        return false;
      }

      if (
        get_Project_Object.geographyId === 2 &&
        !get_Project_Object.licenseProcurementNextSteps
      ) {
        return false;
      }
      if (
        get_Project_Object.geographyId === 2 &&
        !get_Project_Object.countryCorruptionRating
      ) {
        return false;
      }
      if (!get_Project_Object.city) {
        return false;
      }
      if (!get_Project_Object.bidSubmissionDate) {
        return false;
      }
      if (!get_Project_Object.opportunityQualification) {
        return false;
      }

      return true;
    }
    if (activeStep === 1) {
      let validEmails: boolean = false;
      projectCustomerPOCs.forEach((each) => {
        if (each.emailId.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
          validEmails = true;
        } else {
          validEmails = false;
        }
      });
      if (!isValidArr(projectCustomerPOCs) || !validEmails) {
        return false;
      }
      return true;
    }
    if (activeStep === 2) {
      if (
        wpfQuestions.goFactorQuestions.length !== Object.keys(goFactors).length
      ) {
        return false;
      }
      if (
        wpfQuestions.getFactorQuestions.length !==
        Object.keys(getFactors).length
      ) {
        return false;
      }
      if (
        (get_Project_Object.manualGoFactorPercentage ||
          get_Project_Object.manualGetFactorPercentage) &&
        !get_Project_Object.manualGoGetFactorRemarks
      ) {
        return false;
      }
      return true;
    }
    if (activeStep === 3) {
      // For bid pipeline section
      if (!((loggedInUser.roleId === 1 || loggedInUser.roleId === 2) && particularProjectResponse?.projectStatus === "Drafted") || (loggedInUser.roleId >= 5 && (particularProjectResponse?.projectStatus && goNoGoFormNotAllowedStatuses.indexOf(particularProjectResponse?.projectStatus) === -1))) {
        return true;
      }

      if (!get_Project_Object.isPqrequired) {
        return false;
      }
      if (!get_Project_Object.isPriceVariation) {
        return false;
      }
      if (!get_Project_Object.tenderMaturity) {
        return false;
      }
      if (!get_Project_Object.competitionIntensityId) {
        return false;
      }
      if (!get_Project_Object.tenderingBandwidthId) {
        return false;
      }
      if (!get_Project_Object.proposalStage) {
        return false;
      }
      if (!get_Project_Object.briefScope) {
        return false;
      }
      if (!get_Project_Object.expectedCompetitors) {
        return false;
      }
      if (get_Project_Object.isOM === true && !get_Project_Object.omduration) {
        return false;
      }

      return true;
    }
    if (activeStep === 4) {
      return true;
    }
    return false;
  };

  // To submit the project win probability factors
  const submitWinProbabilityFactors = (projectId: number) => {
    if (
      Object.keys(goFactors).length !== 0 ||
      Object.keys(getFactors).length !== 0
    ) {
      const formData = {
        ...goFactors,
        ...getFactors,
      };
      WPFService.postWinFactorOptions(projectId, formData);
    }
  };

  // To submit the project customerPOCs
  const submitCustomerPOCs = (projectId: number) => {
    if (isValidArr(projectCustomerPOCs)) {
      const formData = projectCustomerPOCs.map((rec) => ({
        ...rec,
        projectId: projectId,
      }));
      CustomerPOCService.postProjectCustomerPOCs(projectId, formData);
    }
  };

  // To submit the project details
  const submitProject = async () => {
    let currentProjectId: number = get_Project_Object.projectId;
    if (currentProjectId === 0) {
      const bidResponse = await ProjectService.postProject(
        loggedInUser.userId,
        get_Project_Object
      );
      if (bidResponse && bidResponse.projectId) {
        currentProjectId = bidResponse.projectId;
        dispatch(setProjecId(bidResponse.projectId)); // to set the projectid on post success
      }
    } else {
      await ProjectService.putProject(
        loggedInUser.userId,
        get_Project_Object.projectId,
        get_Project_Object
      );
    }

    return currentProjectId;
  };

  const submitProjectOtherInfo = async (
    currentProjectId: number,
    status: Status
  ) => {
    // To submit win probability factors
    submitWinProbabilityFactors(currentProjectId);

    // To submit project customer POCS
    submitCustomerPOCs(currentProjectId);

    if (mode === "user") {
      // To submit the project status
      const projectStatus: PostProjectStatusModel = {
        status: status,
        projectId: currentProjectId,
        approverUserId: loggedInUser.userId,
        approverRoleId: loggedInUser.roleId,
      };
      dispatch(postParticularProjectStatus(projectStatus));

      // To submit project comments
      if (projectCreationComment.commentText.trim()) {
        let emailArray: string[] = [];
        let result = projectCreationComment.commentText
          .trim()
          .split(/[" ",]/)
          .filter((each) => each.startsWith("@"));
        result.forEach((eachName) => {
          if (newUsers.hasOwnProperty(eachName.slice(1))) {
            emailArray.push(newUsers[eachName.slice(1)]);
          }
        });
        dispatch(
          setParticularProjectParticularComment({
            ...projectCreationComment,
            projectId: currentProjectId,
            mentionedEmails: emailArray,
          })
        );
      }
    }
    // To submit project documents
    if (projectDocuments.length) {
      await ProjectService.postProjectDocuments(
        currentProjectId,
        projectDocuments
      );
    }

    // Fetch projects on success
    fetchProjectsOnSubmit();
  };

  const handleSubmit = async () => {
    const currentProjectId = await submitProject();

    if (!currentProjectId) {
      return;
    }

    submitProjectOtherInfo(
      currentProjectId,
      particularProjectResponse?.isDrafted && loggedInUser.roleId === 2
        ? Status.isSubmitted
        : Status.isDrafted
    );

    if (particularProjectResponse?.isDrafted) {
      displayToast("Project bid submitted", "success");
    }
    else {
      displayToast("Project bid created", "success");
    }
    handleCloseBidPanel();
  };

  const handleSaveAsDraft = async () => {
    if (!get_Project_Object.projectName.trim()) {
      displayToast("Project name is required", "error");
      return;
    }

    const currentProjectId = await submitProject();

    if (!currentProjectId) {
      return;
    }

    submitProjectOtherInfo(currentProjectId, Status.isSavedAsDraft);
    displayToast("Project bid saved as draft", "success");
  };

  useEffect(() => {
    dispatch(fetchSBGs());
    dispatch(fetchSBUs());
    dispatch(fetchBUs());
    dispatch(fetchSegmentLOBs());
    dispatch(fetchSegmentClassifications());
    dispatch(fetchCustomerTypes());
    dispatch(fetchCustomers());
    dispatch(fetchContractTypes());
    dispatch(fetchFinances());
    dispatch(fetchCompetitionIntensities());
    dispatch(fetchTenderingBandwidths());
    dispatch(fetchGeographies());
    dispatch(fetchQuestions());
    if (loggedInUser.roleId >= 5) {
      dispatch(fetchAllProjectsWithStatus());
    } else {
      dispatch(
        fetchProjectsWithStatusForUserId(
          loggedInUser.userId,
          loggedInUser.roleId
        )
      );
    }
    // dispatch(fetchAllProjectsWithStatus());
  }, [dispatch]);
  return (
    <DrawerComponent
      open={isOpen}
      title={(((loggedInUser.roleId === 1 || loggedInUser.roleId === 2) && particularProjectResponse?.projectStatus === "Drafted") || (loggedInUser.roleId >= 5 && (particularProjectResponse?.projectStatus && goNoGoFormNotAllowedStatuses.indexOf(particularProjectResponse?.projectStatus) === -1))) ? "Go No Go" : "Bid Pipeline"}
      onClose={handleCloseBidPanel}
    >
      <Divider />
      <Box pl={2} pr={2} pb={2}>
        <BidPipeLineSection
          steps={steps}
          activeStep={activeStep}
          mode={mode}
          type={type}
          isGoNoGo={((loggedInUser.roleId === 1 || loggedInUser.roleId === 2) && particularProjectResponse?.projectStatus === "Drafted") || (loggedInUser.roleId >= 5 && (particularProjectResponse?.projectStatus && goNoGoFormNotAllowedStatuses.indexOf(particularProjectResponse?.projectStatus) === -1)) ? true : false}
        />
        <NavButtons
          activeStep={activeStep}
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleSaveAsDraft={handleSaveAsDraft}
          isValidToGoNext={isValidToGoNext}
          isSubmitted={particularProjectResponse?.isSubmitted}
          isDrafted={particularProjectResponse?.isDrafted}
          steps={steps}
          mode={mode}
        />
      </Box>
    </DrawerComponent>
  );
};

export default BidCreationLayoutPanel;